import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import './Header.css';
import logoImage from '../images/logos/cyberaries-logo.png';

// Map any known service URL path prefix to its sidebar category key
const getActiveCategory = (pathname) => {
  if (
    pathname.startsWith('/services/regulatory') ||
    pathname.startsWith('/services/regulatory')
  ) return 'regulatory';
  if (
    pathname.startsWith('/services/web-app-security') ||
    pathname.startsWith('/services/mobile-app-security') ||
    pathname.startsWith('/services/thick-client-security') ||
    pathname.startsWith('/services/api-security') ||
    pathname.startsWith('/services/sast-dast') ||
    pathname.startsWith('/services/source-code-review') ||
    pathname.startsWith('/services/red-teaming') ||
    pathname.startsWith('/services/vulnerability-assessment')
  ) return 'offensive';
  if (
    pathname.startsWith('/services/cloud') ||
    pathname.startsWith('/services/infrastructure') ||
    pathname.startsWith('/services/network-security') ||
    pathname.startsWith('/services/wireless-security') ||
    pathname.startsWith('/services/firewall-rule-review')
  ) return 'infrastructure';
  if (
    pathname.startsWith('/services/compliance') ||
    pathname.startsWith('/services/soc-compliance')
  ) return 'compliance';
  if (pathname.startsWith('/services/privacy') ||
    pathname === '/services/compliance/iso-27701' ||
    pathname === '/services/compliance/iso-27018'
  ) return 'privacy';
  if (pathname.startsWith('/services/consulting')) return 'consulting';
  return 'regulatory'; // default
};

const Header = () => {
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [closingDropdown, setClosingDropdown] = useState(null);

  const [timeoutId, setTimeoutId] = useState(null);
  const [closeTimeoutId, setCloseTimeoutId] = useState(null);

  // Services sidebar tab — driven by URL, overrideable by hover click
  const [activeServiceTab, setActiveServiceTab] = useState(() => getActiveCategory(location.pathname));

  // Mobile menu states
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileActiveMenu, setMobileActiveMenu] = useState(null);
  const [mobileActiveService, setMobileActiveService] = useState(() => getActiveCategory(location.pathname));

  const isActive = (path) => location.pathname === path;

  // Scroll effect for premium sticky navbar (shrinks + increases blur/opacity on scroll)
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Desktop hover handlers
  const handleMouseEnter = (dropdown) => {
    // Only work on desktop
    if (window.innerWidth > 968) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (closeTimeoutId) {
        clearTimeout(closeTimeoutId);
        setCloseTimeoutId(null);
      }
      setClosingDropdown(null);
      setActiveDropdown(dropdown);
    }
  };

  const handleMouseLeave = () => {
    // Only work on desktop
    if (window.innerWidth > 968) {
      const id = setTimeout(() => {
        setActiveDropdown((current) => {
          if (current) {
            setClosingDropdown(current);
            // Keep the dropdown mounted just long enough to play its
            // exit animation, then unmount it entirely.
            const closeId = setTimeout(() => setClosingDropdown(null), 280);
            setCloseTimeoutId(closeId);
          }
          return null;
        });

      }, 150);
      setTimeoutId(id);
    }
  };

  const handleTabClick = (e) => {
    const serviceName = e.currentTarget.getAttribute('data-service');
    setActiveServiceTab(serviceName);
  };

  // Sync activeServiceTab with URL when route changes
  useEffect(() => {
    setActiveServiceTab(getActiveCategory(location.pathname));
    setMobileActiveService(getActiveCategory(location.pathname));
  }, [location.pathname]);

  // Mobile menu handlers
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setMobileActiveMenu(null);
    setMobileActiveService('regulatory');

    // Prevent body scroll when menu is open
    if (!isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  };

  const toggleMobileDropdown = (menu) => {
    setMobileActiveMenu(mobileActiveMenu === menu ? null : menu);
  };

  const handleMobileServiceTab = (serviceName) => {
    setMobileActiveService(serviceName);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setMobileActiveMenu(null);
    document.body.style.overflow = '';
  };

  // Close mobile menu on route change
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 968 && isMobileMenuOpen) {
        closeMobileMenu();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      if (timeoutId) clearTimeout(timeoutId);
      if (closeTimeoutId) clearTimeout(closeTimeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header className={`main-header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        {/* Logo */}
        <Link to="/" className="header-logo" onClick={closeMobileMenu}>
          <span className="logo-container">
            <img
              src={logoImage}
              alt="Cyber Aries Security Solutions"
              className="logo-image"
            />
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="header-nav">
          {/* Home */}
          <div className="nav-item">
            <Link
              to="/"
              className={`nav-link ${isActive("/") ? "active" : ""}`}
            >
              Home
            </Link>
          </div>

          {/* Company */}
          <div
            className="nav-item"
            onMouseEnter={() => handleMouseEnter("company")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className={`nav-link ${activeDropdown === "company" ? "dropdown-open" : ""}`}
            >
              Company
              <ChevronDown size={14} className={`nav-chevron ${activeDropdown === "company" ? "rotated" : ""}`} />
            </button>

            {(activeDropdown === "company" || closingDropdown === "company") && (
              <div className={`dropdown-menu dropdown-menu--company ${activeDropdown === "company" ? "is-open" : "is-closing"}`}>
                <span className="dropdown-pointer" aria-hidden="true" />
                <Link to="/about" className="dropdown-item">
                  About Us
                </Link>
                <Link to="/careers" className="dropdown-item">
                  Career
                </Link>
              </div>
            )}
          </div>

          {/* Services */}
          <div
            className="nav-item"
            onMouseEnter={() => handleMouseEnter("services")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className={`nav-link ${activeDropdown === "services" ? "dropdown-open" : ""}`}
            >
              Services
              <ChevronDown size={14} className={`nav-chevron ${activeDropdown === "services" ? "rotated" : ""}`} />
            </button>

            {(activeDropdown === "services" || closingDropdown === "services") && (
              <div className={`services-mega-menu ${activeDropdown === "services" ? "is-open" : "is-closing"}`}>
                <span className="dropdown-pointer dropdown-pointer--services" aria-hidden="true" />
                {/* Sidebar */}
                <div className="services-sidebar">
                  {[
                    { key: 'regulatory', label: 'Regulatory Audit' },
                    { key: 'offensive', label: 'Offensive Security' },
                    { key: 'infrastructure', label: 'Infrastructure & Cloud' },
                    { key: 'compliance', label: 'Compliance' },
                    { key: 'privacy', label: 'Data Privacy' },
                    { key: 'consulting', label: 'Strategic Consulting' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      className={`services-category-btn${activeServiceTab === key ? ' active' : ''}`}
                      data-service={key}
                      onClick={handleTabClick}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Content Area */}
                <div className="services-content-area">
                  {/* Regulatory Audit */}
                  <div
                    className={`services-category-content${activeServiceTab === 'regulatory' ? ' active' : ''}`}
                    data-service-panel="regulatory"
                  >
                    <div className="services-grid">
                      <div className="services-section">
                        <h5 className="services-section-title">
                          Banking & Finance
                        </h5>
                        <Link
                          to="/services/regulatory/rbi-cyber-framework"
                          className="services-link"
                        >
                          RBI - Cyber Framework for Banks
                        </Link>
                        <Link
                          to="/services/regulatory/rbi-nbfc-guidelines"
                          className="services-link"
                        >
                          RBI - NBFC Cyber Guidelines
                        </Link>
                        <Link
                          to="/services/regulatory/rbi-payment-systems"
                          className="services-link"
                        >
                          RBI - Payment Systems Audit
                        </Link>
                      </div>
                      <div className="services-section">
                        <h5 className="services-section-title">
                          Capital Markets
                        </h5>
                        <Link
                          to="/services/regulatory/bse-cyber-audit"
                          className="services-link"
                        >
                          BSE Cyber Security Audit
                        </Link>
                        <Link
                          to="/services/regulatory/nse-cyber-audit"
                          className="services-link"
                        >
                          NSE Cyber Security Audit
                        </Link>
                        <Link
                          to="/services/regulatory/sebi-cyber-audit"
                          className="services-link"
                        >
                          SEBI Cyber Security Audit
                        </Link>
                      </div>
                      <div className="services-section">
                        <h5 className="services-section-title">
                          Government & Digital
                        </h5>
                        <Link
                          to="/services/regulatory/data-localization"
                          className="services-link"
                        >
                          Data Localization Audit
                        </Link>
                        <Link
                          to="/services/regulatory/irdai-isnp"
                          className="services-link"
                        >
                          IRDAI - ISNP Security Audit
                        </Link>
                        <Link
                          to="/services/regulatory/uidai-compliance"
                          className="services-link"
                        >
                          UIDAI - AUA/KUA Compliance
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Offensive Security */}
                  <div
                    className={`services-category-content${activeServiceTab === 'offensive' ? ' active' : ''}`}
                    data-service-panel="offensive"
                  >
                    <div className="services-grid">
                      <div className="services-section">
                        <h5 className="services-section-title">
                          Application Security
                        </h5>
                        <Link
                          to="/services/web-app-security"
                          className="services-link"
                        >
                          Web App Security (WAST)
                        </Link>
                        <Link
                          to="/services/mobile-app-security"
                          className="services-link"
                        >
                          Mobile App Security (MAST)
                        </Link>
                        <Link
                          to="/services/thick-client-security"
                          className="services-link"
                        >
                          Thick Client Security
                        </Link>
                        <Link
                          to="/services/api-security"
                          className="services-link"
                        >
                          API Security Testing
                        </Link>
                      </div>
                      <div className="services-section">
                        <h5 className="services-section-title">
                          Code Security
                        </h5>
                        <Link
                          to="/services/sast-dast"
                          className="services-link"
                        >
                          SAST & DAST
                        </Link>
                        <Link
                          to="/services/source-code-review"
                          className="services-link"
                        >
                          Source Code Review
                        </Link>
                      </div>
                      <div className="services-section">
                        <h5 className="services-section-title">
                          Advanced Testing
                        </h5>
                        <Link
                          to="/services/red-teaming"
                          className="services-link"
                        >
                          Red Teaming
                        </Link>
                        <Link
                          to="/services/vulnerability-assessment"
                          className="services-link"
                        >
                          VAPT Services
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Infrastructure & Cloud */}
                  <div
                    className={`services-category-content${activeServiceTab === 'infrastructure' ? ' active' : ''}`}
                    data-service-panel="infrastructure"
                  >
                    <div className="services-grid">
                      <div className="services-section">
                        <h5 className="services-section-title">
                          IT Infrastructure
                        </h5>
                        <Link
                          to="/services/infrastructure/network-architecture"
                          className="services-link"
                        >
                          Network Architecture Design
                        </Link>
                        <Link
                          to="/services/infrastructure/server-storage"
                          className="services-link"
                        >
                          Server & Storage Setup
                        </Link>
                        <Link
                          to="/services/infrastructure/virtualization"
                          className="services-link"
                        >
                          Virtualization Setup
                        </Link>
                        <Link
                          to="/services/infrastructure/endpoint-management"
                          className="services-link"
                        >
                          Endpoint Management
                        </Link>
                      </div>
                      <div className="services-section">
                        <h5 className="services-section-title">
                          Cloud Security
                        </h5>
                        <Link
                          to="/services/cloud/config-review"
                          className="services-link"
                        >
                          Cloud Config Review
                        </Link>
                        <Link
                          to="/services/cloud/penetration-testing"
                          className="services-link"
                        >
                          Cloud Penetration Testing
                        </Link>
                        <Link
                          to="/services/cloud/container-k8s-security"
                          className="services-link"
                        >
                          Container & K8s Security
                        </Link>
                      </div>
                      <div className="services-section">
                        <h5 className="services-section-title">
                          Network Security
                        </h5>
                        <Link
                          to="/services/network-security"
                          className="services-link"
                        >
                          Network Security Testing
                        </Link>
                        <Link
                          to="/services/wireless-security"
                          className="services-link"
                        >
                          Wireless Security
                        </Link>
                        <Link
                          to="/services/firewall-rule-review"
                          className="services-link"
                        >
                          Firewall Rule Review
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Compliance */}
                  <div
                    className={`services-category-content${activeServiceTab === 'compliance' ? ' active' : ''}`}
                    data-service-panel="compliance"
                  >
                    <div className="services-grid">
                      <div className="services-section">
                        <h5 className="services-section-title">
                          ISO Standards
                        </h5>
                        <Link
                          to="/services/compliance/iso-27001"
                          className="services-link"
                        >
                          ISO 27001
                        </Link>
                        <Link
                          to="/services/compliance/iso-22301"
                          className="services-link"
                        >
                          ISO 22301
                        </Link>
                        <Link
                          to="/services/compliance/iso-20000"
                          className="services-link"
                        >
                          ISO 20000
                        </Link>
                        <Link
                          to="/services/compliance/iso-42001"
                          className="services-link"
                        >
                          ISO 42001
                        </Link>
                      </div>
                      <div className="services-section">
                        <h5 className="services-section-title">
                          Global Attestations
                        </h5>
                        <Link
                          to="/services/soc-compliance/soc-1"
                          className="services-link"
                        >
                          SOC 1
                        </Link>
                        <Link
                          to="/services/soc-compliance/soc-2"
                          className="services-link"
                        >
                          SOC 2
                        </Link>
                        <Link
                          to="/services/compliance/pci-dss"
                          className="services-link"
                        >
                          PCI DSS
                        </Link>
                        <Link
                          to="/services/compliance/swift"
                          className="services-link"
                        >
                          SWIFT Assessment
                        </Link>
                        <Link
                          to="/services/compliance/fedramp"
                          className="services-link"
                        >
                          FedRAMP Assessment
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Data Privacy */}
                  <div
                    className={`services-category-content${activeServiceTab === 'privacy' ? ' active' : ''}`}
                    data-service-panel="privacy"
                  >
                    <div className="services-grid">
                      <div className="services-section">
                        <h5 className="services-section-title">
                          Global Privacy Laws
                        </h5>
                        <Link
                          to="/services/privacy/gdpr"
                          className="services-link"
                        >
                          GDPR Compliance
                        </Link>
                        <Link
                          to="/services/privacy/ccpa"
                          className="services-link"
                        >
                          CCPA Compliance
                        </Link>
                        <Link
                          to="/services/privacy/hipaa"
                          className="services-link"
                        >
                          HIPAA Compliance
                        </Link>
                      </div>
                      <div className="services-section">
                        <h5 className="services-section-title">
                          Indian Privacy Laws
                        </h5>
                        <Link
                          to="/services/privacy/dpdp"
                          className="services-link"
                        >
                          DPDP Act Compliance
                        </Link>
                        <Link
                          to="/services/privacy/dpia"
                          className="services-link"
                        >
                          DPIA Services
                        </Link>
                      </div>
                      <div className="services-section">
                        <h5 className="services-section-title">
                          Privacy Frameworks
                        </h5>
                        <Link
                          to="/services/compliance/iso-27701"
                          className="services-link"
                        >
                          ISO 27701
                        </Link>
                        <Link
                          to="/services/compliance/iso-27018"
                          className="services-link"
                        >
                          ISO 27018
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Strategic Consulting */}
                  <div
                    className={`services-category-content${activeServiceTab === 'consulting' ? ' active' : ''}`}
                    data-service-panel="consulting"
                  >
                    <div className="services-grid">
                      <div className="services-section">
                        <h5 className="services-section-title">
                          Forensics & Recovery
                        </h5>
                        <Link
                          to="/services/consulting/forensic-audit"
                          className="services-link"
                        >
                          Forensic Audit
                        </Link>
                        <Link
                          to="/services/consulting/ransomware-recovery"
                          className="services-link"
                        >
                          Ransomware Recovery
                        </Link>
                      </div>
                      <div className="services-section">
                        <h5 className="services-section-title">
                          Managed Leadership
                        </h5>
                        <Link
                          to="/services/consulting/virtual-ciso"
                          className="services-link"
                        >
                          Virtual CISO Services
                        </Link>
                        <Link
                          to="/services/consulting/dpo-service"
                          className="services-link"
                        >
                          DPO as a Service
                        </Link>
                      </div>
                      <div className="services-section">
                        <h5 className="services-section-title">
                          US Federal Standards
                        </h5>
                        <Link
                          to="/services/consulting/nist-800-53"
                          className="services-link"
                        >
                          NIST 800-53
                        </Link>
                        <Link
                          to="/services/consulting/nist-800-171"
                          className="services-link"
                        >
                          NIST 800-171
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Industries */}
          <div
            className="nav-item"
            onMouseEnter={() => handleMouseEnter("industries")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className={`nav-link ${activeDropdown === "industries" ? "dropdown-open" : ""}`}
            >
              Industries
              <ChevronDown size={14} className={`nav-chevron ${activeDropdown === "industries" ? "rotated" : ""}`} />
            </button>

            {(activeDropdown === "industries" || closingDropdown === "industries") && (
              <div className={`dropdown-menu dropdown-menu--industries ${activeDropdown === "industries" ? "is-open" : "is-closing"}`}>
                <span className="dropdown-pointer" aria-hidden="true" />
                <Link to="/industries/bfsi" className="dropdown-item">
                  BFSI
                </Link>
                <Link to="/industries/government" className="dropdown-item">
                  Government
                </Link>
                <Link to="/industries/technology" className="dropdown-item">
                  Technology
                </Link>
                <Link to="/industries/education" className="dropdown-item">
                  Education
                </Link>
                <Link to="/industries/healthcare" className="dropdown-item">
                  Healthcare
                </Link>
                <Link to="/industries/manufacturing" className="dropdown-item">
                  Manufacturing
                </Link>
              </div>
            )}
          </div>

          {/* Resources */}
          <div
            className="nav-item"
            onMouseEnter={() => handleMouseEnter("resources")}
            onMouseLeave={handleMouseLeave}
          >
            <button
              className={`nav-link ${activeDropdown === "resources" ? "dropdown-open" : ""}`}
            >
              Resources
              <ChevronDown size={14} className={`nav-chevron ${activeDropdown === "resources" ? "rotated" : ""}`} />
            </button>

            {(activeDropdown === "resources" || closingDropdown === "resources") && (
              <div className={`dropdown-menu dropdown-menu--resources ${activeDropdown === "resources" ? "is-open" : "is-closing"}`}>
                <span className="dropdown-pointer" aria-hidden="true" />
                <Link to="/resources/case-studies" className="dropdown-item">
                  Case Studies
                </Link>
                <a
                  href="https://medium.com/@cyberaries"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dropdown-item"
                >
                  Blogs
                </a>
                <Link to="/resources/news" className="dropdown-item">
                  News & Insights
                </Link>
              </div>
            )}
          </div>

          {/* Career */}
          {/* <div className="nav-item">
            <Link to="/careers" className={`nav-link ${isActive('/careers') ? 'active' : ''}`}>
              Career
            </Link>
          </div> */}

          {/* Contact Us */}
          <div className="nav-item">
            <Link
              to="/contact"
              className={`nav-link ${isActive("/contact") ? "active" : ""}`}
            >
              Contact Us
            </Link>
          </div>

          {/* Consult Button */}
          <Link
            to="/appointment"
            className="quick-contact-btn desktop-only"
          >
            Consult With Us
          </Link>
          
        </nav>

        {/* Mobile Menu Toggle */}
        <button
          className={`mobile-menu-toggle ${isMobileMenuOpen ? 'open' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
        >
          <span className="hamburger">
            <span className="hamburger-line" />
            <span className="hamburger-line" />
            <span className="hamburger-line" />
          </span>
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <>
          <div className="mobile-overlay" onClick={closeMobileMenu}></div>
          <nav className="mobile-menu">
            <div className="mobile-menu-content">
              {/* Home */}
              <Link
                to="/"
                className="mobile-nav-link"
                onClick={closeMobileMenu}
              >
                Home
              </Link>

              {/* Company */}
              <div className="mobile-nav-item">
                <button
                  className="mobile-nav-link"
                  onClick={() => toggleMobileDropdown("company")}
                >
                  Company
                  <ChevronDown
                    size={18}
                    className={`mobile-chevron ${mobileActiveMenu === "company" ? "rotated" : ""}`}
                  />
                </button>
                {mobileActiveMenu === "company" && (
                  <div className="mobile-dropdown">
                    <Link
                      to="/about"
                      className="mobile-dropdown-item"
                      onClick={closeMobileMenu}
                    >
                      About Us
                    </Link>
                    <Link
                      to="/careers"
                      className="mobile-dropdown-item"
                      onClick={closeMobileMenu}
                    >
                      Career
                    </Link>
                  </div>
                )}
              </div>

              {/* Services */}
              <div className="mobile-nav-item">
                <button
                  className="mobile-nav-link"
                  onClick={() => toggleMobileDropdown("services")}
                >
                  Services
                  <ChevronDown
                    size={18}
                    className={`mobile-chevron ${mobileActiveMenu === "services" ? "rotated" : ""}`}
                  />
                </button>
                {mobileActiveMenu === "services" && (
                  <div className="mobile-services-wrapper">
                    {/* Service Tabs */}
                    <div className="mobile-service-tabs">
                      <button
                        className={`mobile-service-tab ${mobileActiveService === "regulatory" ? "active" : ""}`}
                        onClick={() => handleMobileServiceTab("regulatory")}
                      >
                        Regulatory Audit
                      </button>
                      <button
                        className={`mobile-service-tab ${mobileActiveService === "offensive" ? "active" : ""}`}
                        onClick={() => handleMobileServiceTab("offensive")}
                      >
                        Offensive Security
                      </button>
                      <button
                        className={`mobile-service-tab ${mobileActiveService === "infrastructure" ? "active" : ""}`}
                        onClick={() => handleMobileServiceTab("infrastructure")}
                      >
                        Infrastructure & Cloud
                      </button>
                      <button
                        className={`mobile-service-tab ${mobileActiveService === "compliance" ? "active" : ""}`}
                        onClick={() => handleMobileServiceTab("compliance")}
                      >
                        Compliance
                      </button>
                      <button
                        className={`mobile-service-tab ${mobileActiveService === "privacy" ? "active" : ""}`}
                        onClick={() => handleMobileServiceTab("privacy")}
                      >
                        Data Privacy
                      </button>
                      <button
                        className={`mobile-service-tab ${mobileActiveService === "consulting" ? "active" : ""}`}
                        onClick={() => handleMobileServiceTab("consulting")}
                      >
                        Strategic Consulting
                      </button>
                    </div>

                    {/* Service Content */}
                    <div className="mobile-service-content">
                      {/* Regulatory Audit */}
                      {mobileActiveService === "regulatory" && (
                        <div className="mobile-service-links">
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              Banking & Finance
                            </h6>
                            <Link
                              to="/services/regulatory/rbi-cyber-framework"
                              onClick={closeMobileMenu}
                            >
                              RBI - Cyber Framework for Banks
                            </Link>
                            <Link
                              to="/services/regulatory/rbi-nbfc-guidelines"
                              onClick={closeMobileMenu}
                            >
                              RBI - NBFC Cyber Guidelines
                            </Link>
                            <Link
                              to="/services/regulatory/rbi-payment-systems"
                              onClick={closeMobileMenu}
                            >
                              RBI - Payment Systems Audit
                            </Link>
                          </div>
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              Capital Markets
                            </h6>
                            <Link
                              to="/services/regulatory/bse-cyber-audit"
                              onClick={closeMobileMenu}
                            >
                              BSE Cyber Security Audit
                            </Link>
                            <Link
                              to="/services/regulatory/nse-cyber-audit"
                              onClick={closeMobileMenu}
                            >
                              NSE Cyber Security Audit
                            </Link>
                            <Link
                              to="/services/regulatory/sebi-cyber-audit"
                              onClick={closeMobileMenu}
                            >
                              SEBI Cyber Security Audit
                            </Link>
                          </div>
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              Government & Digital
                            </h6>
                            <Link
                              to="/services/regulatory/data-localization"
                              onClick={closeMobileMenu}
                            >
                              Data Localization Audit
                            </Link>
                            <Link
                              to="/services/regulatory/irdai-isnp"
                              onClick={closeMobileMenu}
                            >
                              IRDAI - ISNP Security Audit
                            </Link>
                            <Link
                              to="/services/regulatory/uidai-compliance"
                              onClick={closeMobileMenu}
                            >
                              UIDAI - AUA/KUA Compliance
                            </Link>
                          </div>
                        </div>
                      )}

                      {/* Offensive Security */}
                      {mobileActiveService === "offensive" && (
                        <div className="mobile-service-links">
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              Application Security
                            </h6>
                            <Link
                              to="/services/web-app-security"
                              onClick={closeMobileMenu}
                            >
                              Web App Security (WAST)
                            </Link>
                            <Link
                              to="/services/mobile-app-security"
                              onClick={closeMobileMenu}
                            >
                              Mobile App Security (MAST)
                            </Link>
                            <Link
                              to="/services/thick-client-security"
                              onClick={closeMobileMenu}
                            >
                              Thick Client Security
                            </Link>
                            <Link
                              to="/services/api-security"
                              onClick={closeMobileMenu}
                            >
                              API Security Testing
                            </Link>
                          </div>
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              Code Security
                            </h6>
                            <Link
                              to="/services/sast-dast"
                              onClick={closeMobileMenu}
                            >
                              SAST & DAST
                            </Link>
                            <Link
                              to="/services/source-code-review"
                              onClick={closeMobileMenu}
                            >
                              Source Code Review
                            </Link>
                          </div>
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              Advanced Testing
                            </h6>
                            <Link
                              to="/services/red-teaming"
                              onClick={closeMobileMenu}
                            >
                              Red Teaming
                            </Link>
                            <Link
                              to="/services/vulnerability-assessment"
                              onClick={closeMobileMenu}
                            >
                              VAPT Services
                            </Link>
                          </div>
                        </div>
                      )}

                      {/* Infrastructure & Cloud */}
                      {mobileActiveService === "infrastructure" && (
                        <div className="mobile-service-links">
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              IT Infrastructure
                            </h6>
                            <Link
                              to="/services/infrastructure/network-architecture"
                              onClick={closeMobileMenu}
                            >
                              Network Architecture Design
                            </Link>
                            <Link
                              to="/services/infrastructure/server-storage"
                              onClick={closeMobileMenu}
                            >
                              Server & Storage Setup
                            </Link>
                            <Link
                              to="/services/infrastructure/virtualization"
                              onClick={closeMobileMenu}
                            >
                              Virtualization Setup
                            </Link>
                            <Link
                              to="/services/infrastructure/endpoint-management"
                              onClick={closeMobileMenu}
                            >
                              Endpoint Management
                            </Link>
                          </div>
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              Cloud Security
                            </h6>
                            <Link
                              to="/services/cloud/config-review"
                              onClick={closeMobileMenu}
                            >
                              Cloud Config Review
                            </Link>
                            <Link
                              to="/services/cloud/penetration-testing"
                              onClick={closeMobileMenu}
                            >
                              Cloud Penetration Testing
                            </Link>
                            <Link
                              to="/services/cloud/container-k8s-security"
                              onClick={closeMobileMenu}
                            >
                              Container & K8s Security
                            </Link>
                          </div>
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              Network Security
                            </h6>
                            <Link
                              to="/services/network-security"
                              onClick={closeMobileMenu}
                            >
                              Network Security Testing
                            </Link>
                            <Link
                              to="/services/wireless-security"
                              onClick={closeMobileMenu}
                            >
                              Wireless Security
                            </Link>
                            <Link
                              to="/services/firewall-rule-review"
                              onClick={closeMobileMenu}
                            >
                              Firewall Rule Review
                            </Link>
                          </div>
                        </div>
                      )}

                      {/* Compliance */}
                      {mobileActiveService === "compliance" && (
                        <div className="mobile-service-links">
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              ISO Standards
                            </h6>
                            <Link
                              to="/services/compliance/iso-27001"
                              onClick={closeMobileMenu}
                            >
                              ISO 27001
                            </Link>
                            <Link
                              to="/services/compliance/iso-22301"
                              onClick={closeMobileMenu}
                            >
                              ISO 22301
                            </Link>
                            <Link
                              to="/services/compliance/iso-20000"
                              onClick={closeMobileMenu}
                            >
                              ISO 20000
                            </Link>
                            <Link
                              to="/services/compliance/iso-42001"
                              onClick={closeMobileMenu}
                            >
                              ISO 42001
                            </Link>
                          </div>
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              Global Attestations
                            </h6>
                            <Link
                              to="/services/soc-compliance/soc-1"
                              onClick={closeMobileMenu}
                            >
                              SOC 1
                            </Link>
                            <Link
                              to="/services/soc-compliance/soc-2"
                              onClick={closeMobileMenu}
                            >
                              SOC 2
                            </Link>
                            <Link
                              to="/services/compliance/pci-dss"
                              onClick={closeMobileMenu}
                            >
                              PCI DSS
                            </Link>
                            <Link
                              to="/services/compliance/swift"
                              onClick={closeMobileMenu}
                            >
                              SWIFT Assessment
                            </Link>
                            <Link
                              to="/services/compliance/fedramp"
                              onClick={closeMobileMenu}
                            >
                              FedRAMP Assessment
                            </Link>
                          </div>
                        </div>
                      )}

                      {/* Data Privacy */}
                      {mobileActiveService === "privacy" && (
                        <div className="mobile-service-links">
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              Global Privacy Laws
                            </h6>
                            <Link
                              to="/services/privacy/gdpr"
                              onClick={closeMobileMenu}
                            >
                              GDPR Compliance
                            </Link>
                            <Link
                              to="/services/privacy/ccpa"
                              onClick={closeMobileMenu}
                            >
                              CCPA Compliance
                            </Link>
                            <Link
                              to="/services/privacy/hipaa"
                              onClick={closeMobileMenu}
                            >
                              HIPAA Compliance
                            </Link>
                          </div>
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              Indian Privacy Laws
                            </h6>
                            <Link
                              to="/services/privacy/dpdp"
                              onClick={closeMobileMenu}
                            >
                              DPDP Act Compliance
                            </Link>
                            <Link
                              to="/services/privacy/dpia"
                              onClick={closeMobileMenu}
                            >
                              DPIA Services
                            </Link>
                          </div>
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              Privacy Frameworks
                            </h6>
                            <Link
                              to="/services/compliance/iso-27701"
                              onClick={closeMobileMenu}
                            >
                              ISO 27701
                            </Link>
                            <Link
                              to="/services/compliance/iso-27018"
                              onClick={closeMobileMenu}
                            >
                              ISO 27018
                            </Link>
                          </div>
                        </div>
                      )}

                      {/* Strategic Consulting */}
                      {mobileActiveService === "consulting" && (
                        <div className="mobile-service-links">
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              Forensics & Recovery
                            </h6>
                            <Link
                              to="/services/consulting/forensic-audit"
                              onClick={closeMobileMenu}
                            >
                              Forensic Audit
                            </Link>
                            <Link
                              to="/services/consulting/ransomware-recovery"
                              onClick={closeMobileMenu}
                            >
                              Ransomware Recovery
                            </Link>
                          </div>
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              Managed Leadership
                            </h6>
                            <Link
                              to="/services/consulting/virtual-ciso"
                              onClick={closeMobileMenu}
                            >
                              Virtual CISO Services
                            </Link>
                            <Link
                              to="/services/consulting/dpo-service"
                              onClick={closeMobileMenu}
                            >
                              DPO as a Service
                            </Link>
                          </div>
                          <div className="mobile-service-group">
                            <h6 className="mobile-service-group-title">
                              US Federal Standards
                            </h6>
                            <Link
                              to="/services/consulting/nist-800-53"
                              onClick={closeMobileMenu}
                            >
                              NIST 800-53
                            </Link>
                            <Link
                              to="/services/consulting/nist-800-171"
                              onClick={closeMobileMenu}
                            >
                              NIST 800-171
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Industries */}
              <div className="mobile-nav-item">
                <button
                  className="mobile-nav-link"
                  onClick={() => toggleMobileDropdown("industries")}
                >
                  Industries
                  <ChevronDown
                    size={18}
                    className={`mobile-chevron ${mobileActiveMenu === "industries" ? "rotated" : ""}`}
                  />
                </button>
                {mobileActiveMenu === "industries" && (
                  <div className="mobile-dropdown">
                    <Link
                      to="/industries/bfsi"
                      className="mobile-dropdown-item"
                      onClick={closeMobileMenu}
                    >
                      BFSI
                    </Link>
                    <Link
                      to="/industries/government"
                      className="mobile-dropdown-item"
                      onClick={closeMobileMenu}
                    >
                      Government
                    </Link>
                    <Link
                      to="/industries/technology"
                      className="mobile-dropdown-item"
                      onClick={closeMobileMenu}
                    >
                      Technology
                    </Link>
                    <Link
                      to="/industries/education"
                      className="mobile-dropdown-item"
                      onClick={closeMobileMenu}
                    >
                      Education
                    </Link>
                    <Link
                      to="/industries/healthcare"
                      className="mobile-dropdown-item"
                      onClick={closeMobileMenu}
                    >
                      Healthcare
                    </Link>
                    <Link
                      to="/industries/manufacturing"
                      className="mobile-dropdown-item"
                      onClick={closeMobileMenu}
                    >
                      Manufacturing
                    </Link>
                  </div>
                )}
              </div>

              {/* Resources */}
              <div className="mobile-nav-item">
                <button
                  className="mobile-nav-link"
                  onClick={() => toggleMobileDropdown("resources")}
                >
                  Resources
                  <ChevronDown
                    size={18}
                    className={`mobile-chevron ${mobileActiveMenu === "resources" ? "rotated" : ""}`}
                  />
                </button>
                {mobileActiveMenu === "resources" && (
                  <div className="mobile-dropdown">
                    <Link
                      to="/resources/case-studies"
                      className="mobile-dropdown-item"
                      onClick={closeMobileMenu}
                    >
                      Case Studies
                    </Link>
                    <a
                      href="https://medium.com/@cyberaries"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mobile-dropdown-item"
                      onClick={closeMobileMenu}
                    >
                      Blogs
                    </a>
                    <Link
                      to="/resources/news"
                      className="mobile-dropdown-item"
                      onClick={closeMobileMenu}
                    >
                      News & Insights
                    </Link>
                  </div>
                )}
              </div>

              {/* Career */}
              <Link
                to="/careers"
                className="mobile-nav-link"
                onClick={closeMobileMenu}
              >
                Career
              </Link>

              {/* Contact Us */}
              <Link
                to="/contact"
                className="mobile-nav-link"
                onClick={closeMobileMenu}
              >
                Contact Us
              </Link>

              {/* Mobile Consult Button */}
              <Link
                to="/appointment"
                className="mobile-consult-btn"
                onClick={closeMobileMenu}
              >
                Consult With Us
              </Link>
            </div>
          </nav>
        </>
      )}
    </header>
  );
};

export default Header;